/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import classNames from 'classnames';

import { FocusScope } from '@coursera/cds-core';

import { useToggleTabContext } from 'bundles/cds-labs/components/ToggleTab/hooks/useToggleTabContext';

import _t from 'i18n!nls/cds-labs';

export interface ToggleTabsProps {
  children: React.ReactNode;
  'aria-label'?: string;
  className?: string;
}

const styles = {
  container: css`
    position: relative;
    display: flex;
    padding: 3px;
    justify-content: space-between;
    align-items: center;
    border-radius: var(--cds-border-radius-max);
    width: fit-content;
    gap: var(--cds-spacing-50);
    margin: var(--cds-spacing-300) 0;
    background-color: var(--cds-color-emphasis-neutral-background-weak);
    border: 1px solid var(--cds-color-grey-100);

    /* Invert overrides */
    &.toggle-tabs-invert {
      background-color: var(--cds-color-lighten-200);
      border: 1px solid var(--cds-color-grey-400);
    }
  `,

  slidingIndicator: css`
    position: absolute;
    top: 3px;
    height: calc(100% - 6px);
    border-radius: var(--cds-border-radius-max);
    transition: all 0.1s ease-in-out;
    pointer-events: none;
    z-index: 1;
    background-color: var(--cds-color-interactive-secondary);

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }

    /* Invert overrides */
    .toggle-tabs-invert & {
      background-color: var(--cds-color-white-0);
    }
  `,
};

const ToggleTabs = ({ children, 'aria-label': ariaLabel, className }: ToggleTabsProps) => {
  const { invert, id, value, updateIndicatorPosition, indicatorStyle } = useToggleTabContext();

  // Update indicator when active tab changes
  React.useLayoutEffect(() => {
    updateIndicatorPosition();
  }, [value, updateIndicatorPosition]);

  // Handle window resize
  React.useLayoutEffect(() => {
    const handleResize = () => {
      updateIndicatorPosition();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateIndicatorPosition]);

  return (
    <FocusScope>
      <div
        css={styles.container}
        className={classNames(className, {
          'toggle-tabs-invert': invert,
        })}
        role="tablist"
        aria-label={ariaLabel || _t('Tab Toggle')}
        id={id}
      >
        <div
          css={styles.slidingIndicator}
          style={{
            left: `${indicatorStyle.left}px`,
            width: `${indicatorStyle.width}px`,
          }}
          data-testid="tab-sliding-indicator"
          aria-hidden="true"
        />

        {children}
      </div>
    </FocusScope>
  );
};

export default ToggleTabs;
